import * as React from "react";
import {
  Badge,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { useNavigate } from "react-router-dom";
import {
  useListUpdatePolling,
  type ListUpdateNotificationGroup,
  type ListUpdateNotificationItem,
  type ListUpdatePollStatus,
} from "../../hooks/useListUpdatePolling";

/**
 * 将剩余毫秒格式化为 mm:ss 或 Ns。
 */
const formatCountdown = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  if (totalSeconds >= 60) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }
  return `${totalSeconds}s`;
};

/**
 * 根据轮询状态生成状态文案与图标。
 */
const getStatusView = (
  status: ListUpdatePollStatus,
  countdownLabel: string
): { label: string; icon: React.ReactNode } => {
  if (status === "polling") {
    return {
      label: "正在查询最新数据…",
      icon: <CircularProgress size={14} thickness={5} color="inherit" />,
    };
  }
  if (status === "paused") {
    return {
      label: "页面隐藏，已暂停轮询",
      icon: <PauseCircleOutlineIcon sx={{ fontSize: 16 }} />,
    };
  }
  return {
    label: `监听中 · ${countdownLabel} 后再次查询`,
    icon: <SyncIcon sx={{ fontSize: 16 }} />,
  };
};

interface StackedTypeCardProps {
  group: ListUpdateNotificationGroup;
  onOpen: (item: ListUpdateNotificationItem) => void;
}

/**
 * 同类型未读堆叠卡片：顶卡可点进详情，角标展示数量。
 */
const StackedTypeCard: React.FC<StackedTypeCardProps> = ({ group, onOpen }) => {
  const top = group.items[0];
  const count = group.items.length;
  const stackDepth = Math.min(count - 1, 2);

  if (!top) {
    return null;
  }

  return (
    <Box sx={{ position: "relative", mb: stackDepth > 0 ? 1.5 : 0 }}>
      {Array.from({ length: stackDepth }).map((_, index) => {
        const depth = stackDepth - index;
        return (
          <Box
            key={`${group.type}-stack-${depth}`}
            sx={{
              position: "absolute",
              left: depth * 4,
              right: -depth * 2,
              top: depth * 4,
              height: "100%",
              borderRadius: 1.5,
              bgcolor: "grey.100",
              border: "1px solid",
              borderColor: "divider",
              zIndex: index,
            }}
          />
        );
      })}

      <Badge
        badgeContent={count}
        color="error"
        overlap="rectangular"
        sx={{
          width: "100%",
          display: "block",
          "& .MuiBadge-badge": {
            right: 10,
            top: 10,
            zIndex: 3,
          },
        }}
      >
        <Paper
          elevation={2}
          onClick={() => onOpen(top)}
          sx={{
            position: "relative",
            zIndex: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
            cursor: "pointer",
            border: "1px solid",
            borderColor: "primary.light",
            bgcolor: "background.paper",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: 4,
            },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {group.typeLabel} · 点击进入详情
          </Typography>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, color: "text.primary", mt: 0.25 }}
          >
            {top.title}
          </Typography>
          {count > 1 ? (
            <Typography variant="caption" color="text.secondary">
              另有 {count - 1} 条同类型未读
            </Typography>
          ) : null}
        </Paper>
      </Badge>
    </Box>
  );
};

/**
 * 全局列表更新面板：常驻轮询状态；未读按类型堆叠，点击进详情并标记已读。
 */
export const ListUpdateNotification: React.FC = () => {
  const navigate = useNavigate();
  const { groups, markAsRead, status, nextPollAt } = useListUpdatePolling();
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const timerId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(timerId);
    };
  }, []);

  const remainingMs = nextPollAt ? nextPollAt - now : 0;
  const countdownLabel = formatCountdown(remainingMs);
  const statusView = getStatusView(status, countdownLabel);
  const unreadCount = groups.reduce((sum, group) => sum + group.items.length, 0);

  /**
   * 点击堆叠顶卡：跳转详情并标记该条已读。
   */
  const handleOpen = (item: ListUpdateNotificationItem) => {
    markAsRead(item.id);
    navigate(item.path);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        width: 320,
        maxWidth: "calc(100vw - 32px)",
      }}
    >
      <Paper
        elevation={6}
        sx={{
          overflow: "hidden",
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          spacing={0.25}
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: status === "polling" ? "primary.main" : "grey.900",
            color: "common.white",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {statusView.icon}
            <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>
              数据监听
            </Typography>
            {unreadCount > 0 ? (
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                未读 {unreadCount}
              </Typography>
            ) : null}
          </Stack>
          <Typography variant="caption" sx={{ opacity: 0.92, pl: 3.25 }}>
            {statusView.label}
          </Typography>
        </Stack>

        <Box sx={{ px: 1.5, py: 1.25 }}>
          {groups.length > 0 ? (
            <Stack spacing={1.25}>
              {groups.map((group) => (
                <StackedTypeCard
                  key={group.type}
                  group={group}
                  onOpen={handleOpen}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {status === "polling"
                ? "正在检查沟通、货源、订单、认证…"
                : "暂无未读消息，保持监听中"}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
